import { useState, useMemo, useCallback, useEffect, memo } from "react";
import { ItemList, Item } from "@/components/ui/item";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Creator } from "@/types/types";
import { toast } from "sonner";
import { DownloadIcon, ArrowUpIcon, ArrowDownIcon } from "lucide-react";
import { ExportCreatorsDialogProps } from "../types";
import { useCreatorsTableContextOptional } from "../CreatorsTableContext";
import { creatorInbox } from "../columns/InboxColumnCell";

interface ColumnConfig {
  key: string;
  defaultName: string;
  isDynamic: boolean;
}

interface ColumnRowProps {
  col: ColumnConfig;
  index: number;
  totalCount: number;
  isSelected: boolean;
  customName: string;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onSelectChange: (key: string, checked: boolean) => void;
  onNameChange: (key: string, name: string) => void;
}

const STATIC_COLUMNS: ColumnConfig[] = [
  { key: "username", defaultName: "Username", isDynamic: false },
  { key: "email", defaultName: "Email", isDynamic: false },
  { key: "platform", defaultName: "Platform", isDynamic: false },
  { key: "platform_id", defaultName: "Platform ID", isDynamic: false },
  { key: "followers", defaultName: "Followers", isDynamic: false },
  { key: "average_views", defaultName: "Avg Views", isDynamic: false },
  { key: "median_views", defaultName: "Median Views", isDynamic: false },
  { key: "status", defaultName: "Status", isDynamic: false },
  { key: "country", defaultName: "Country", isDynamic: false },
  { key: "negotiated_price", defaultName: "Negotiated Price", isDynamic: false },
];

// Org-wide (Global CRM) extras — which campaign the row lives in and the
// mailbox it was worked from.
const ORG_WIDE_COLUMNS: ColumnConfig[] = [
  { key: "campaign_name", defaultName: "Campaign", isDynamic: false },
  { key: "inbox", defaultName: "Inbox", isDynamic: false },
];

const ColumnRow = memo(function ColumnRow({
  col,
  index,
  totalCount,
  isSelected,
  customName,
  onMoveUp,
  onMoveDown,
  onSelectChange,
  onNameChange,
}: ColumnRowProps) {
  return (
    <Item variant="row" size="sm" className="flex-nowrap gap-2 py-1">
      <div className="flex flex-row">
        <Button
          variant="ghost"
          size="icon-xs"
          scope={false}
          onClick={() => onMoveUp(index)}
          disabled={index === 0}
        >
          <ArrowUpIcon className="size-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon-xs"
          scope={false}
          onClick={() => onMoveDown(index)}
          disabled={index === totalCount - 1}
        >
          <ArrowDownIcon className="size-3.5" />
        </Button>
      </div>
      <Checkbox
        id={`col-${col.key}`}
        checked={isSelected}
        onCheckedChange={(checked) => onSelectChange(col.key, !!checked)}
      />
      <Label
        htmlFor={`col-${col.key}`}
        className="w-1/3 shrink-0 text-sm cursor-pointer"
      >
        {col.defaultName}
        {col.isDynamic && (
          <span className="text-xs text-muted-foreground ml-1">(custom)</span>
        )}
      </Label>
      <Input
        value={customName}
        onChange={(e) => onNameChange(col.key, e.target.value)}
        placeholder={col.defaultName}
        disabled={!isSelected}
        className="h-9 text-sm"
      />
    </Item>
  );
});

export default function ExportCreatorsDialog({
  selectedCount,
  creators,
  campaignFields,
}: ExportCreatorsDialogProps) {
  const [open, setOpen] = useState(false);
  const tableContext = useCreatorsTableContextOptional();
  const orgWide = tableContext?.orgWide ?? false;
  const campaignFor = tableContext?.campaignFor;

  const staticColumns = useMemo(
    () => (orgWide ? [...STATIC_COLUMNS, ...ORG_WIDE_COLUMNS] : STATIC_COLUMNS),
    [orgWide]
  );

  // Derive dynamic field names from campaign fields
  const dynamicFieldNames = useMemo(
    () => campaignFields?.map((f) => f.name) || [],
    [campaignFields]
  );

  const allColumns = useMemo(() => {
    const dynamicColumns: ColumnConfig[] = dynamicFieldNames.map((name) => ({
      key: `dynamic_${name}`,
      defaultName: name,
      isDynamic: true,
    }));
    return [...staticColumns, ...dynamicColumns];
  }, [staticColumns, dynamicFieldNames]);

  const columnMap = useMemo(() => {
    return new Map(allColumns.map((col) => [col.key, col]));
  }, [allColumns]);

  const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({});
  const [columnNames, setColumnNames] = useState<Record<string, string>>({});
  const [columnOrder, setColumnOrder] = useState<string[]>([]);

  // Reset state when dialog opens to ensure fresh state with current fields
  useEffect(() => {
    if (open) {
      const selected: Record<string, boolean> = {};
      const names: Record<string, string> = {};

      staticColumns.forEach((col) => {
        selected[col.key] = true;
        names[col.key] = col.defaultName;
      });

      dynamicFieldNames.forEach((name) => {
        selected[`dynamic_${name}`] = true;
        names[`dynamic_${name}`] = name;
      });

      const order = [
        ...staticColumns.map((col) => col.key),
        ...dynamicFieldNames.map((name) => `dynamic_${name}`),
      ];

      setSelectedColumns(selected);
      setColumnNames(names);
      setColumnOrder(order);
    }
  }, [open, staticColumns, dynamicFieldNames]);

  const orderedColumns = useMemo(() => {
    return columnOrder
      .map((key) => columnMap.get(key))
      .filter((col): col is ColumnConfig => col !== undefined);
  }, [columnOrder, columnMap]);

  const selectedColumnCount = useMemo(
    () => Object.values(selectedColumns).filter(Boolean).length,
    [selectedColumns]
  );

  const moveUp = useCallback((index: number) => {
    if (index === 0) return;
    setColumnOrder((prev) => {
      const newOrder = [...prev];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      return newOrder;
    });
  }, []);

  const moveDown = useCallback((index: number) => {
    setColumnOrder((prev) => {
      if (index === prev.length - 1) return prev;
      const newOrder = [...prev];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      return newOrder;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedColumns((prev) => {
      const newSelected = { ...prev };
      Object.keys(newSelected).forEach((key) => {
        newSelected[key] = true;
      });
      return newSelected;
    });
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedColumns((prev) => {
      const newSelected = { ...prev };
      Object.keys(newSelected).forEach((key) => {
        newSelected[key] = false;
      });
      return newSelected;
    });
  }, []);

  const handleColumnSelect = useCallback((key: string, checked: boolean) => {
    setSelectedColumns((prev) => ({ ...prev, [key]: checked }));
  }, []);

  const handleColumnRename = useCallback((key: string, name: string) => {
    setColumnNames((prev) => ({ ...prev, [key]: name }));
  }, []);

  const handleExport = useCallback(() => {
    const columnsToExport = orderedColumns.filter(
      (col) => selectedColumns[col.key] !== false
    );

    if (columnsToExport.length === 0) {
      toast.error("Please select at least one column to export");
      return;
    }

    const headers = columnsToExport.map(
      (col) => columnNames[col.key] || col.defaultName
    );

    const getValueForColumn = (creator: Creator, columnKey: string): unknown => {
      if (columnKey.startsWith("dynamic_")) {
        const fieldName = columnKey.replace("dynamic_", "");
        return creator.fields?.find((f) => f.name === fieldName)?.value || "";
      }
      if (columnKey === "campaign_name") return campaignFor?.(creator)?.campaign_name ?? "";
      if (columnKey === "inbox") return creatorInbox(creator, campaignFor?.(creator))?.email ?? "";
      return creator[columnKey as keyof Creator];
    };

    const escapeCsvCell = (cell: unknown): string => {
      if (cell === null || cell === undefined) return '""';
      const str = String(cell);
      const escaped = str.replace(/"/g, '""');
      const mightBeNumber = /^[+\-]?\d{8,}$|^[+\-]\d+$|^\d+[eE]\d+$/.test(str.trim());
      const prefix = mightBeNumber ? "\t" : "";
      return `"${prefix}${escaped}"`;
    };

    const rows = creators.map((creator) =>
      columnsToExport.map((col) => getValueForColumn(creator, col.key))
    );

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => escapeCsvCell(cell)).join(","))
      .join("\n");

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `creators-export-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);

    setOpen(false);
    toast.success(`Exported ${creators.length} creator(s) to CSV`);
  }, [orderedColumns, selectedColumns, columnNames, creators, campaignFor]);

  if (selectedCount === 0) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="xs">
          <DownloadIcon
            className="opacity-60"
            size={16}
            aria-hidden="true"
          />
          Export CSV
          <Badge variant="count" className="-me-1">
            {selectedCount}
          </Badge>
        </Button>
      </DialogTrigger>
      <DialogContent className="p-5 sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Export to CSV</DialogTitle>
          <DialogDescription>
            Select columns to export and optionally rename them.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 py-2">
          <Button variant="secondary" size="sm" onClick={handleSelectAll}>
            Select All
          </Button>
          <Button variant="secondary" size="sm" onClick={handleDeselectAll}>
            Deselect All
          </Button>
          <span className="text-sm text-muted-foreground ml-auto">
            {selectedColumnCount} of {allColumns.length} selected
          </span>
        </div>

        <ItemList className="max-h-96">
          {orderedColumns.map((col, index) => (
            <ColumnRow
              key={col.key}
              col={col}
              index={index}
              totalCount={orderedColumns.length}
              isSelected={selectedColumns[col.key] ?? true}
              customName={columnNames[col.key] ?? col.defaultName}
              onMoveUp={moveUp}
              onMoveDown={moveDown}
              onSelectChange={handleColumnSelect}
              onNameChange={handleColumnRename}
            />
          ))}
        </ItemList>

        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={selectedColumnCount === 0}>
            <DownloadIcon />
            Export CSV
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
